import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import { companyAddressLines, companyRegistrationLine, type CompanySettings } from '../../../lib/companySettings'
import { registerPdfLongWordBreaking } from '../../../lib/pdfWrap'

registerPdfLongWordBreaking()

// Company name/address/phone/registration/BIFA text all come from `note.company`
// (company_settings, see 20260916b_company_settings_address_update.sql) -- never hardcode
// them here, the caller fetches via getCompanySettings() before building NoteData.

const S = StyleSheet.create({
  page:       { padding: 36, fontFamily: 'Helvetica', fontSize: 9, color: '#222' },
  // Header
  header:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  logoBox:    { flex: 1 },
  logoText:   { fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#1e3a5f', letterSpacing: 2 },
  addrText:   { fontSize: 7.5, color: '#555', marginTop: 2 },
  // Title
  title:      { fontSize: 20, fontFamily: 'Helvetica-Bold', textAlign: 'center', marginBottom: 10, color: '#111' },
  // Top-right refs
  refBox:     { alignItems: 'flex-end', minWidth: 160 },
  refRow:     { flexDirection: 'row', marginBottom: 2 },
  refLbl:     { fontSize: 7.5, color: '#777', width: 75, textAlign: 'right', marginRight: 4 },
  refVal:     { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#1d4ed8' },
  // Two-column address boxes
  addrRow:    { flexDirection: 'row', gap: 8, marginBottom: 10 },
  addrBox:    { flex: 1, border: 1, borderColor: '#d1d5db', borderRadius: 3, padding: '5 7' },
  addrBoxHdr: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#555', textTransform: 'uppercase', marginBottom: 3, borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb', paddingBottom: 2 },
  addrLine:   { fontSize: 8, marginBottom: 1.5 },
  addrSub:    { fontSize: 7.5, color: '#666', marginBottom: 1 },
  // Goods table
  table:      { marginBottom: 8 },
  thead:      { flexDirection: 'row', backgroundColor: '#1e3a5f', padding: '3 6' },
  trow:       { flexDirection: 'row', padding: '3 6', borderBottomWidth: 0.5, borderBottomColor: '#f3f4f6' },
  trowAlt:    { flexDirection: 'row', padding: '3 6', borderBottomWidth: 0.5, borderBottomColor: '#f3f4f6', backgroundColor: '#f9fafb' },
  th:         { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#fff', textTransform: 'uppercase' },
  td:         { fontSize: 8 },
  // Totals bar
  totalsRow:  { flexDirection: 'row', backgroundColor: '#f3f4f6', padding: '4 6', marginBottom: 8, borderWidth: 0.5, borderColor: '#d1d5db' },
  totItem:    { flex: 1, alignItems: 'center' },
  totLbl:     { fontSize: 7, color: '#666', marginBottom: 1 },
  totVal:     { fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#111' },
  // Remarks / haulier / notes
  section:    { marginBottom: 6 },
  sLbl:       { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#555', textTransform: 'uppercase', marginBottom: 2 },
  sVal:       { fontSize: 8, color: '#222' },
  // Delivery refs
  childRef:   { fontSize: 7.5, color: '#444', marginBottom: 1.5 },
  // Signature (Serial No now lives inside the release-authorisation sig box, see below)
  sigBox:     { flexDirection: 'row', gap: 12, marginTop: 8 },
  sig:        { flex: 1, borderWidth: 0.5, borderColor: '#9ca3af', borderRadius: 3, padding: '5 7', minHeight: 52 },
  sigHdr:     { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#444', marginBottom: 3, borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb', paddingBottom: 2 },
  sigField:   { fontSize: 7.5, color: '#888', marginBottom: 12 },
  // B7: blank fillable "Collected By" block -- driver/vehicle/ID, sits bottom-right below
  // the two authorise/receive signature boxes (a driver only fills this in at pickup, after
  // the "I authorise..." box above is already signed by the releasing party).
  collectedByRow: { flexDirection: 'row', marginTop: 8 },
  collectedBy:    { width: '50%', marginLeft: '50%', borderWidth: 0.5, borderColor: '#9ca3af', borderRadius: 3, padding: '5 7' },
  collectedByHdr: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#444', marginBottom: 3, borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb', paddingBottom: 2 },
  // Footer
  footer:     { marginTop: 10, borderTopWidth: 0.5, borderTopColor: '#d1d5db', paddingTop: 5 },
  footerLine: { fontSize: 6, color: '#888', lineHeight: 1.3 },
  footerRow:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 },
  footerTxt:  { fontSize: 6.5, color: '#aaa' },
})

export interface NoteItem {
  group_name:      string | null
  description:     string | null
  pallets_qty:     number | null
  cartons:         number
  kgs:             number | null
  cbm:             number | null
  delivery_address: string | null
  marks_numbers?:  string | null
  system_refs?:    string | null
}

export interface NoteData {
  company:            CompanySettings
  note_ref:           string
  note_type:          'collection' | 'delivery'
  job_ref:            string | null
  customer_ref:       string | null        // composed child refs e.g. 'KI16835M-06,08+...'
  planned_release_at: string | null
  contact_name:       string | null
  contact_phone:      string | null
  vehicle_reg:        string | null
  driver_name:        string | null
  haulier:            string | null
  delivery_address:   string | null        // Release To address
  collect_from:       string | null        // warehouse address (Collect From box)
  marks_numbers:      string | null        // MBL number
  container_no:       string | null
  seal_no:            string | null
  container_type:     string | null
  remarks:            string | null
  delivery_refs:      string[]             // per-child-job delivery ref codes
  notes:              string | null
  warehouse_name:     string
  issued_at:          string
  serial_no:          number | null
}

function fmt(d: string | null | undefined) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
    .replace(/ /g, '-')
}

export function NotePDF({ note, items }: { note: NoteData; items: NoteItem[] }) {
  const isCollection = note.note_type === 'collection'
  const totalPlts = items.reduce((s, i) => s + (i.pallets_qty ?? 0), 0)
  const totalCtns = items.reduce((s, i) => s + i.cartons, 0)
  const totalKgs  = items.reduce((s, i) => s + (i.kgs ?? 0), 0)
  const totalCbm  = items.reduce((s, i) => s + (i.cbm ?? 0), 0)

  const containerStr = [note.container_no, note.seal_no && `SEAL: ${note.seal_no}`, note.container_type]
    .filter(Boolean).join(' — ')

  return (
    <Document>
      <Page size="A4" style={S.page}>

        {/* 1. Header: CY UK logo left, address top-right */}
        <View style={S.header}>
          <View style={S.logoBox}>
            <Text style={S.logoText}>CY UK</Text>
          </View>
          <View style={S.refBox}>
            {companyAddressLines(note.company).map((line, i) => <Text key={i} style={S.addrText}>{line}</Text>)}
            {note.company.phone && <Text style={S.addrText}>T: {note.company.phone}</Text>}
          </View>
        </View>

        {/* 3. Customer Ref + Job Reference, top right */}
        <View style={[S.header, { marginBottom: 12 }]}>
          <View style={S.logoBox} />
          <View style={S.refBox}>
            {note.customer_ref && (
              <View style={S.refRow}>
                <Text style={S.refLbl}>Customer Ref:</Text>
                <Text style={[S.refVal, { fontSize: 7, color: '#374151' }]}>{note.customer_ref}</Text>
              </View>
            )}
            <View style={S.refRow}>
              <Text style={S.refLbl}>Job Reference:</Text>
              <Text style={S.refVal}>{note.job_ref || '—'}</Text>
            </View>
            <View style={S.refRow}>
              <Text style={S.refLbl}>Note Ref:</Text>
              <Text style={[S.refVal, { color: '#374151' }]}>{note.note_ref}</Text>
            </View>
          </View>
        </View>

        {/* 2. Title */}
        <Text style={S.title}>{isCollection ? 'Collection Note' : 'Delivery Note'}</Text>

        {/* 4+5. Collect From / Release To boxes */}
        <View style={S.addrRow}>
          <View style={S.addrBox}>
            <Text style={S.addrBoxHdr}>Collect From</Text>
            <Text style={S.addrLine}>{note.collect_from || note.warehouse_name}</Text>
            {note.planned_release_at && (
              <Text style={S.addrSub}>Collection Date: {fmt(note.planned_release_at)}</Text>
            )}
            {note.company.phone && <Text style={S.addrSub}>T: {note.company.phone}</Text>}
          </View>
          <View style={S.addrBox}>
            <Text style={S.addrBoxHdr}>{isCollection ? 'Release To' : 'Deliver To'}</Text>
            <Text style={S.addrLine}>{note.delivery_address || '—'}</Text>
            {note.contact_name && <Text style={S.addrSub}>Contact: {note.contact_name}</Text>}
            {note.contact_phone && <Text style={S.addrSub}>Tel: {note.contact_phone}</Text>}
          </View>
        </View>

        {/* 6. Goods table: Marks & Numbers / Description / NOP / Weight / Cube / Summary */}
        <View style={S.table}>
          <View style={S.thead}>
            <Text style={[S.th, { width: 90 }]}>Marks &amp; Numbers</Text>
            <Text style={[S.th, { flex: 1 }]}>Description</Text>
            <Text style={[S.th, { width: 40, textAlign: 'right' }]}>NOP</Text>
            <Text style={[S.th, { width: 50, textAlign: 'right' }]}>Weight</Text>
            <Text style={[S.th, { width: 45, textAlign: 'right' }]}>Cube</Text>
            <Text style={[S.th, { width: 70 }]}>Summary</Text>
          </View>
          {items.map((it, i) => {
            // Summary: pallet/carton rollup for this row (e.g. "5 PLTS / 120 CTNS") -- the
            // compact package-count summary freight paperwork conventionally carries here.
            const summary = [it.pallets_qty ? `${it.pallets_qty} PLTS` : null, `${it.cartons} CTNS`]
              .filter(Boolean).join(' / ')
            return (
              <View key={i} style={i % 2 === 1 ? S.trowAlt : S.trow}>
                <Text style={[S.td, { width: 90, color: '#555' }]}>
                  {it.marks_numbers || note.marks_numbers || '—'}
                  {it.system_refs ? `\n${it.system_refs}` : ''}
                </Text>
                <Text style={[S.td, { flex: 1 }]}>{it.description || it.group_name || '—'}</Text>
                <Text style={[S.td, { width: 40, textAlign: 'right', fontFamily: 'Helvetica-Bold' }]}>{it.cartons}</Text>
                <Text style={[S.td, { width: 50, textAlign: 'right' }]}>{it.kgs ? it.kgs.toFixed(0) : '—'}</Text>
                <Text style={[S.td, { width: 45, textAlign: 'right' }]}>{it.cbm ? it.cbm.toFixed(2) : '—'}</Text>
                <Text style={[S.td, { width: 70, fontSize: 7 }]}>{summary}</Text>
              </View>
            )
          })}
        </View>

        {/* Totals */}
        <View style={S.totalsRow}>
          {[
            { l: 'TOTAL PALLETS',  v: totalPlts ? `${totalPlts} PLTS` : '—' },
            { l: 'TOTAL CARTONS',  v: String(totalCtns) },
            { l: 'TOTAL WEIGHT',   v: totalKgs ? totalKgs.toFixed(0) : '—' },
            { l: 'TOTAL CUBE',     v: totalCbm ? totalCbm.toFixed(2) : '—' },
          ].map(({ l, v }) => (
            <View key={l} style={S.totItem}>
              <Text style={S.totLbl}>{l}</Text>
              <Text style={S.totVal}>{v}</Text>
            </View>
          ))}
        </View>

        {/* 7. Remarks (container / seal + per-item notes, e.g. FBA warehouse names) */}
        {(containerStr || note.remarks) && (
          <View style={S.section}>
            <Text style={S.sLbl}>Remarks</Text>
            {containerStr && <Text style={S.sVal}>Container: {containerStr}</Text>}
            {note.remarks?.split('\n').filter(Boolean).map((line, i) => (
              <Text key={i} style={S.sVal}>{line}</Text>
            ))}
          </View>
        )}

        {/* 8. Haulier */}
        {note.haulier && (
          <View style={S.section}>
            <Text style={S.sLbl}>Haulier</Text>
            <Text style={S.sVal}>{note.haulier}</Text>
            {note.vehicle_reg && <Text style={[S.sVal, { color: '#666' }]}>Vehicle: {note.vehicle_reg}</Text>}
            {note.driver_name && <Text style={[S.sVal, { color: '#666' }]}>Driver: {note.driver_name}</Text>}
          </View>
        )}

        {/* 9. Notes — child jobs + delivery refs */}
        {(note.delivery_refs?.length > 0 || note.notes) && (
          <View style={S.section}>
            <Text style={S.sLbl}>Notes</Text>
            {note.delivery_refs?.map((ref, i) => (
              <Text key={i} style={S.childRef}>{ref}</Text>
            ))}
            {note.notes && <Text style={S.sVal}>{note.notes}</Text>}
          </View>
        )}

        {/* 10+11. Signature blocks — Serial No now lives inside the release-authorisation box
            (it's a field of that authorisation, not a standalone document number). */}
        <View style={S.sigBox}>
          <View style={S.sig}>
            <Text style={S.sigHdr}>I AUTHORISE YOU TO RELEASE THESE GOODS TO</Text>
            <Text style={S.sigField}>Signature: _______________________________</Text>
            <Text style={S.sigField}>Print Name: ______________________________</Text>
            <Text style={S.sigField}>Serial No: {note.serial_no ?? '_______________'}</Text>
            <Text style={S.sigField}>Time / Date: _____________________________</Text>
          </View>
          <View style={S.sig}>
            <Text style={S.sigHdr}>RECEIVED IN GOOD CONDITION</Text>
            <Text style={S.sigField}>Signature: _______________________________</Text>
            <Text style={S.sigField}>Print Name: ______________________________</Text>
            <Text style={S.sigField}>Time / Date: _____________________________</Text>
          </View>
        </View>

        {/* Collected By — blank fillable, driver only completes this at physical pickup. */}
        <View style={S.collectedByRow}>
          <View style={S.collectedBy}>
            <Text style={S.collectedByHdr}>COLLECTED BY</Text>
            <Text style={S.sigField}>Driver Name: _____________________________</Text>
            <Text style={S.sigField}>Vehicle Reg: _____________________________</Text>
            <Text style={S.sigField}>Photo ID No: _____________________________</Text>
          </View>
        </View>

        {/* 12. Footer: registration + BIFA disclaimer */}
        <View style={S.footer}>
          <Text style={S.footerLine}>{companyRegistrationLine(note.company)} {note.company.bifa_line}</Text>
          <View style={S.footerRow}>
            <Text style={S.footerTxt}>BIFA Member — British International Freight Association</Text>
            <Text style={S.footerTxt}>Issued: {new Date(note.issued_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Text>
          </View>
        </View>

      </Page>
    </Document>
  )
}
